import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchInventoryCounts,
  fetchInventoryCountById,
  createInventoryCount,
  startInventoryCount,
  recordCount,
  completeInventoryCount,
  fetchLowStock,
  fetchExchangeRates,
} from '@/api/inventoryApi';
import type { CreateInventoryCountRequest, RecordCountRequest } from '@/types/inventory';

export function useInventoryCounts(mdPage: number = 1, mdPageSize: number = 20) {
  return useQuery({
    queryKey: ['inventory-counts', mdPage, mdPageSize],
    queryFn: () => fetchInventoryCounts(mdPage, mdPageSize),
  });
}

export function useInventoryCountById(mdId: string) {
  return useQuery({
    queryKey: ['inventory-count', mdId],
    queryFn: () => fetchInventoryCountById(mdId),
    enabled: !!mdId,
  });
}

export function useCreateInventoryCount() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdRequest: CreateInventoryCountRequest) =>
      createInventoryCount(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
  });
}

export function useStartCount() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdId: string) => startInventoryCount(mdId),
    onSuccess: (_mdData, mdId) => {
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-count', mdId] });
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
  });
}

export function useRecordCount(mdCountId: string) {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdRequest: RecordCountRequest) =>
      recordCount(mdCountId, mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-count', mdCountId] });
    },
  });
}

export function useCompleteCount() {
  const mdQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (mdId: string) => completeInventoryCount(mdId),
    onSuccess: (_mdData, mdId) => {
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-count', mdId] });
      mdQueryClient.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
  });
}

export function useLowStock() {
  return useQuery({
    queryKey: ['low-stock'],
    queryFn: fetchLowStock,
  });
}

export function useExchangeRates() {
  return useQuery({
    queryKey: ['exchange-rates'],
    queryFn: fetchExchangeRates,
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchInterval: 60 * 60 * 1000, // auto-refresh every hour
  });
}
